import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faUsers, faUserClock,  faProjectDiagram
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/WorkforceCapacity-Process.png";
import vis from "../../assets/images/use-cases/WorkforceCapacity-Dashboard.png";

const WorkforceCapacityPlanning = () => {
    return (
        <>
            <SEO
                title="Workforce Capacity Planning: AI Digital Twin for HR Operations | Nebulyx AI"
                description="Model your workforce as a skills network connecting people, projects, and demand forecasts. Simulate hiring, training, and attrition scenarios to identify capability gaps before they derail projects."
                keywords="AI digital twin, workforce planning, capacity planning, skills gap, attrition prediction, resource management, HR analytics"
                canonicalUrl="https://nebulyx.ai/use-cases/workforce-capacity-planning"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faUsers}/>
                                            &nbsp;
                                            HR Operations
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Do we have the right skills for next quarter?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your workforce as a skills network connecting people, projects, and demand forecasts. Simulate hiring, training, and attrition scenarios offline, before skill shortages derail delivery.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faUserClock} className="stat-icon"/>
                                <span className="stat-value">40%</span>
                                <span className="stat-label">Faster Gap Closure</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faProjectDiagram} className="stat-icon"/>
                                <span className="stat-value">92%</span>
                                <span className="stat-label">Project Coverage</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">-28%</span>
                                <span className="stat-label">Attrition Impact</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Skills gaps discovered too late</li>
                                            <li>Attrition blindsides critical projects</li>
                                            <li>No visibility into future capacity</li>
                                            <li>Reactive hiring misses deadlines</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a digital twin of your workforce</li>
                                            <li>Model skills, projects, and demand</li>
                                            <li>Overlay attrition risk prediction</li>
                                            <li>Simulate interventions before committing</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Predict gaps quarters in advance</li>
                                            <li>Proactive retention interventions</li>
                                            <li>Optimise build vs buy vs borrow</li>
                                            <li>Hit delivery targets consistently</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect HR systems, model workforce as a skills network, overlay predictive analytics, then simulate capacity scenarios to close gaps before they impact delivery.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>HRIS, project systems, demand forecasts, L&D platforms</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Network</strong>
                                    <span>Map people → skills → roles → projects with proficiencies</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>Gap analysis, attrition prediction, time-to-competency</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate hiring, training, attrition, demand changes</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects HRIS, project management, demand forecasting, and L&D systems to build a comprehensive skills network.
                                            ML models predict attrition risk per employee based on tenure, engagement signals, and career progression,
                                            while constraint-based optimisation matches supply to demand and identifies gaps by skill and time period.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Workforce Command Centre</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises skills capacity as a heatmap showing supply vs demand gaps by competency area.
                                            HR and delivery leaders can see project coverage risk, identify flight-risk employees holding critical skills,
                                            and simulate interventions, hiring, upskilling, redeployment, to see gap closure timelines before committing budget.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Employee Skills Inventory</li>
                                            <li>Project Resource Allocations</li>
                                            <li>Demand / Pipeline Forecasts</li>
                                            <li>Utilisation & Availability</li>
                                            <li>Training Completion History</li>
                                            <li>Attrition & Tenure Data</li>
                                            <li>Cost Rates by Role</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Skills gap heatmap by quarter</li>
                                            <li>Attrition risk scoring per employee</li>
                                            <li>Time-to-competency projections</li>
                                            <li>Project delivery risk forecasting</li>
                                            <li>Build vs buy vs borrow analysis</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Accelerated hiring (+10 FTEs)</li>
                                            <li>Upskilling programme launch</li>
                                            <li>Attrition spike (+25%)</li>
                                            <li>Pipeline demand surge</li>
                                            <li>Key person departure</li>
                                            <li>Contractor bridge strategy</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the workforce as a digital twin and running capacity simulations,
                                        HR and delivery leaders could discover that 3 high flight-risk employees hold 40% of ML/AI capability, a
                                        single-point-of-failure that threatens two strategic projects.
                                        Simulation may reveal that <strong>upskilling 8 mid-level engineers closes the ML gap 60% cheaper than external hiring</strong>,
                                        with a 4-month time-to-competency versus 6-month recruitment cycle, all validated before committing headcount budget.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">Upskill 8 → Close Gap 60% Cheaper</span>
                                        <span className="discovery-note">4-month competency vs 6-month hire</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to See Your Skills Gaps Before They Hit?</h2>
                            <p>
                                If workforce capacity uncertainty is threatening project delivery,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default WorkforceCapacityPlanning;